import React, { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import { fetchJson, getResolvedApiBase } from '../lib/http';
import { formatDate, formatMoney } from '../lib/format';

type Invoice = {
	id: string;
	invoice_number: string;
	issue_date: string;
	status: string;
	sub_total: number;
	tax_total: number;
	balance_due: number;
};

export function InvoicesPage() {
	const [invoices, setInvoices] = useState<Invoice[] | null>(null);
	const [error, setError] = useState('');
	const API_BASE = (getResolvedApiBase() || '').replace(/\/+$/, '');
	const [expandedId, setExpandedId] = useState<string | null>(null);
	const [appsByInvoice, setAppsByInvoice] = useState<Record<string, any[]>>({});
	const [appsLoading, setAppsLoading] = useState<Record<string, boolean>>({});
	const [appsError, setAppsError] = useState<Record<string, string>>({});

	useEffect(() => {
		let cancelled = false;
		(async () => {
			try {
				const data = await fetchJson<Invoice[]>('/portal/invoices');
				if (!cancelled) setInvoices(data);
			} catch (e: any) {
				if (!cancelled) setError('Failed to load invoices.');
			}
		})();
		return () => { cancelled = true; };
	}, []);

	async function toggleExpand(id: string) {
		setExpandedId(prev => (prev === id ? null : id));
		if (!appsByInvoice[id] && !appsLoading[id]) {
			setAppsLoading(prev => ({ ...prev, [id]: true }));
			setAppsError(prev => ({ ...prev, [id]: '' }));
			try {
				const data = await fetchJson<any[]>(`/portal/invoices/${id}/applications`);
				setAppsByInvoice(prev => ({ ...prev, [id]: data }));
			} catch (_e) {
				setAppsError(prev => ({ ...prev, [id]: 'Failed to load payments for this invoice.' }));
			} finally {
				setAppsLoading(prev => ({ ...prev, [id]: false }));
			}
		}
	}

	return (
		<Layout>
			<div className="rounded-2xl border border-gray-200 bg-white elev-1">
				<div className="p-5 border-b border-gray-100">
					<h1 className="text-2xl font-extrabold text-gray-900">Invoices</h1>
				</div>
				<div className="max-h-[70vh] overflow-auto">
					{invoices === null ? (
						<div className="p-5 space-y-2">
							{Array.from({ length: 8 }).map((_, i) => (
								<div key={i} className="h-10 rounded-lg bg-gray-100 animate-pulse" />
							))}
						</div>
					) : invoices.length === 0 ? (
						<div className="p-6 text-sm text-gray-700">No invoices yet</div>
					) : (
						<table className="min-w-full">
							<thead className="sticky top-0 z-10 bg-gray-50/80 backdrop-blur">
								<tr>
									<th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">Invoice #</th>
									<th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">Issued</th>
									<th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">Status</th>
									<th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">Subtotal</th>
									<th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">Tax</th>
									<th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">Balance due</th>
									<th className="px-4 py-2" />
								</tr>
							</thead>
							<tbody className="divide-y divide-gray-100">
								{invoices.map(inv => (
									<React.Fragment key={inv.id}>
									<tr className="odd:bg-white even:bg-gray-50/40 hover:bg-sedra/5 transition-colors">
										<td className="px-4 py-3 text-sm text-gray-900 font-medium">{inv.invoice_number}</td>
										<td className="px-4 py-3 text-sm text-gray-700">{formatDate(inv.issue_date)}</td>
										<td className="px-4 py-3 text-sm">
											<span className="inline-flex items-center rounded-lg border px-2 py-1 text-xs font-semibold uppercase tracking-wide text-gray-700">{inv.status}</span>
										</td>
										<td className="px-4 py-3 text-sm text-gray-700">{formatMoney(inv.sub_total)}</td>
										<td className="px-4 py-3 text-sm text-gray-700">{formatMoney(inv.tax_total)}</td>
										<td className="px-4 py-3 text-sm text-gray-900 font-semibold">{formatMoney(inv.balance_due)}</td>
										<td className="px-4 py-3 text-right">
											<button onClick={() => toggleExpand(inv.id)} aria-expanded={expandedId === inv.id} className="rounded-xl border border-sedra text-sedra px-3 py-1.5 text-sm font-semibold hover:bg-sedra/5 focus-visible:ring-2 focus-visible:ring-sedra focus-visible:ring-offset-2 mr-2">
												{expandedId === inv.id ? 'Hide payments' : 'Show payments'}
											</button>
											<a className="rounded-xl border border-sedra text-sedra px-3 py-1.5 text-sm font-semibold hover:bg-sedra/5 focus-visible:ring-2 focus-visible:ring-sedra focus-visible:ring-offset-2" target="_blank" rel="noreferrer" href={`${API_BASE}/portal/invoices/${inv.id}/pdf`}>
												View PDF
											</a>
										</td>
									</tr>
									{expandedId === inv.id && (
										<tr>
											<td colSpan={7} className="px-4 pb-4">
												<div className="rounded-xl border border-gray-100 bg-gray-50 p-4 mt-1">
													<div className="flex items-center justify-between mb-2">
														<div className="text-sm font-semibold text-gray-800">Payments applied</div>
														<button onClick={() => toggleExpand(inv.id)} className="text-xs text-sedra hover:underline">Hide</button>
													</div>
													{appsLoading[inv.id] && <div className="text-sm text-gray-600">Loading…</div>}
													{appsError[inv.id] && <div className="text-sm text-red-700">{appsError[inv.id]}</div>}
													{!appsLoading[inv.id] && !appsError[inv.id] && (
														appsByInvoice[inv.id]?.length ? (
															<ul className="space-y-2">
																{appsByInvoice[inv.id].map((a: any) => (
																	<li key={`${a.payment_id}-${a.invoice_id}`} className="text-sm text-gray-800">
																		<span className="font-medium">{formatDate(a.date)}</span>
																		<span className="mx-2">—</span>
																		<span>{formatMoney(Number(a.applied_amount))} of {formatMoney(Number(a.payment_amount))}</span>
																		{a.method ? <span className="ml-2 text-gray-600">({a.method}{a.reference ? ` • ${a.reference}` : ''})</span> : null}
																	</li>
																))}
															</ul>
														) : (
															<div className="text-sm text-gray-600">No payments applied yet.</div>
														)
													)}
												</div>
											</td>
										</tr>
									)}
									</React.Fragment>
								))}
							</tbody>
						</table>
					)}
					{error && <div className="p-6 text-sm text-red-700">{error}</div>}
				</div>
			</div>
		</Layout>
	);
} 